import React, { Component } from "react";
import { Map } from "immutable";
import styled from "styled-components";
import SpeakerBubble from "./SpeakerBubble";
import {
  CompositeDecorator,
  convertFromRaw,
  Editor,
  EditorState,
  getDefaultKeyBinding,
  Modifier,
  SelectionState,
} from "draft-js";

/* Snippet Buttons */

const PlaybackButton = styled.span`
  background-color: #112650;
  white-space: nowrap;
  border-radius: 2px;
  font-size: 0.75em;
  padding: 0.25em;
  vertical-align: middle;
`;

const SVGIconContainer = styled.svg`
  width: 8px;
  height: 8px;
  margin-right: 2px;
`;

export default class TranscriptEditor extends Component {
  blockRenderMap = Map({
    CustomerBubble: {
      element: "div",
    },
    RepBubble: {
      element: "div",
    },
    unstyled: {
      element: "div",
      wrapper: <div />,
    },
  });

  constructor(props) {
    super(props);
    let { cachedContentState } = this.props;

    this.handleKeyCommand = this.handleKeyCommand.bind(this);
    this.updatePlayingSnippetButton = this.updatePlayingSnippetButton.bind(
      this
    );
    this.SnippetPlaybackButton = this.SnippetPlaybackButton.bind(this);

    this.snippetButtonDecorator = new CompositeDecorator([
      {
        strategy: this.timestampStrategy,
        component: this.SnippetPlaybackButton,
      },
    ]);

    if (cachedContentState === null) {
      cachedContentState = convertFromRaw(this.props.rawContent);
    }

    const editorState = EditorState.createWithContent(
      cachedContentState,
      this.snippetButtonDecorator
    );
    this.props.onChange(editorState);

    this.state = {
      highlighted: false,
      forceUpdate: false,
      hideEventQualityReasons: this.props.hideEventQualityReasons,
    };

    // prettier-ignore
    this.SpeakerBubbleComponent = this.props.SpeakerBubbleComponent || SpeakerBubble;
  }

  shouldComponentUpdate(nextProps, nextState) {
    return !(this.state.forceUpdate && nextState.forceUpdate);
  }

  componentDidUpdate() {
    if (this.props.highlightPreviousPasses !== this.state.highlighted) {
      this.setState({
        forceUpdate: true,
        highlighted: this.props.highlightPreviousPasses,
      });
    } else if (
      this.props.hideEventQualityReasons !== this.state.hideEventQualityReasons
    ) {
      this.setState({
        hideEventQualityReasons: this.props.hideEventQualityReasons,
      });
      let editorState = this.props.editorState;
      let contentState = editorState.getCurrentContent();
      this.forceEditorRefresh(editorState, contentState);
    } else if (this.state.forceUpdate) {
      this.setState({ forceUpdate: false });
    }
  }

  blockStyleFn = block => {
    const metadata = Object.fromEntries(block.getData());

    if (this.state.highlighted && metadata.eventQualityReasons.length > 0) {
      return "transcriptQualityRating-speaker-row-highlight";
    }
  };

  blockRendererFn = block => {
    const type = block.getType();
    const data = block.getData();

    const metadata = Object.fromEntries(data);

    switch (type) {
      case "CustomerBubble":
        return {
          component: this.SpeakerBubbleComponent,
          editable: metadata["needsRevision"],
          props: {
            onIconClick: this.changeBubbleType,
            type: "CustomerBubble",
            secondPass: this.props.secondPass,
            needsRevision: metadata["needsRevision"],
            eventQualityReasons: metadata["eventQualityReasons"],
            hasEvents: this.props.hasEvents,
            hideEventQualityReasons: this.state.hideEventQualityReasons,
            data: data,
            ...(this.props.SpeakerBubbleBlockProps ?? {}),
          },
        };
      case "RepBubble":
        return {
          component: this.SpeakerBubbleComponent,
          editable: metadata["needsRevision"],
          props: {
            onIconClick: this.changeBubbleType,
            type: "RepBubble",
            secondPass: this.props.secondPass,
            needsRevision: metadata["needsRevision"],
            eventQualityReasons: metadata["eventQualityReasons"],
            hasEvents: this.props.hasEvents,
            hideEventQualityReasons: this.state.hideEventQualityReasons,
            data: data,
            ...(this.props.SpeakerBubbleBlockProps ?? {}),
          },
        };
      default:
        return null;
    }
  };

  generateSelectionState(
    blockKey,
    anchorKey,
    anchorOffset,
    focusKey,
    focusOffset
  ) {
    let sourceSelection = SelectionState.createEmpty(blockKey);
    return sourceSelection.merge({
      anchorKey: anchorKey,
      anchorOffset: anchorOffset,
      focusKey: focusKey,
      focusOffset: focusOffset,
    });
  }

  changeBubbleType = blockKey => {
    let { editorState, readOnly } = this.props;

    if (readOnly) {
      return null;
    }

    let contentState = editorState.getCurrentContent();
    const contentBlock = contentState.getBlockForKey(blockKey);

    let selectionState = SelectionState.createEmpty(contentBlock.getKey());
    selectionState = selectionState.merge({
      anchorKey: contentBlock.getKey(),
      anchorOffset: 0,
      focusKey: contentBlock.getKey(),
      focusOffset: 0,
    });

    editorState = EditorState.push(
      editorState,
      Modifier.setBlockType(
        contentState,
        selectionState,
        contentBlock.getType() === "RepBubble" ? "CustomerBubble" : "RepBubble"
      )
    );

    this.props.onChange(editorState);
  };

  timestampStrategy(contentBlock, callback, contentState) {
    contentBlock.findEntityRanges(character => {
      const entityKey = character.getEntity();
      if (entityKey === null) {
        return false;
      }
      return contentState.getEntity(entityKey).getMutability() === "IMMUTABLE";
    }, callback);
  }

  keyBindingFn(e) {
    /* Probably should be replaced with handleEnter() */
    if (e.keyCode === 13) {
      /* Return key */
      return "split-block";
    }
    return getDefaultKeyBinding(e);
  }

  SnippetPlaybackButton = props => {
    const entity = props.contentState.getEntity(props.entityKey);
    const isPlaying = entity.data.playing;
    return (
      <PlaybackButton
        contentEditable={false}
        onClick={() =>
          isPlaying
            ? this.handleSnippetPauseClick(this.props.timestamp)
            : this.handleSnippetPlayClick(entity.data.timestampMS)
        }
      >
        <SVGIconContainer contentEditable={false}>
          {isPlaying ? (
            <React.Fragment>
              <rect width="3" height="8" fill="white" rx="1" />
              <rect x="5" width="3" height="8" fill="white" rx="1" />
            </React.Fragment>
          ) : (
            <polygon points="0,0 0,8 8,4" fill="white" strokeLinejoin="round" />
          )}
        </SVGIconContainer>
        {props.children}
      </PlaybackButton>
    );
  };

  handleSnippetPlayClick = timestampMS => {
    const seconds = timestampMS / 1000.0;
    this.props.seek(seconds, true);
  };

  handleSnippetPauseClick = timestamp => {
    this.props.seek(timestamp, false);
  };

  forceEditorRefresh(editorState, contentState) {
    editorState = EditorState.push(editorState, contentState);

    /*
    This is a "hack" that makes sure that once we update the data field
    for the entities, a re-render is triggered in the transcript.
    */
    editorState = EditorState.set(editorState, { decorator: null });
    editorState = EditorState.set(editorState, {
      decorator: this.snippetButtonDecorator,
    });
    editorState = EditorState.forceSelection(
      editorState,
      editorState.getSelection()
    );

    this.props.onChange(editorState);
  }

  updatePlayingSnippetButton() {
    let incomingTimestampMS = Math.floor(this.props.timestamp * 1000);
    let isPlaying = this.props.isPlaying;
    let editorState = this.props.editorState;
    let contentState = editorState.getCurrentContent();
    const contentBlocks = contentState.getBlocksAsArray();

    /* find the entity that has the highest timestampMS
        not above the currently playing position */
    let latestKey = "";
    let latestTime = undefined;
    let currentlyPlayingKey = "";
    contentBlocks.forEach(contentBlock => {
      contentBlock.findEntityRanges(character => {
        const entityKey = character.getEntity();
        if (entityKey === null) {
          return;
        }

        const entityData = contentState.getEntity(entityKey).data;
        const entityTime = entityData.timestampMS;

        if (
          entityTime <= incomingTimestampMS &&
          (entityTime >= latestTime || latestTime === undefined)
        ) {
          latestKey = entityKey;
          latestTime = entityTime;
        }

        const entityPlaying = entityData.playing;
        if (entityPlaying) {
          currentlyPlayingKey = entityKey;
        }
      });
    });

    if (isPlaying) {
      if (currentlyPlayingKey !== latestKey) {
        if (currentlyPlayingKey) {
          contentState = contentState.mergeEntityData(currentlyPlayingKey, {
            playing: false,
          });
        }
        if (latestKey) {
          contentState = contentState.mergeEntityData(latestKey, {
            playing: true,
          });
        }
        this.forceEditorRefresh(editorState, contentState);
      }
    } else {
      if (currentlyPlayingKey) {
        contentState = contentState.mergeEntityData(currentlyPlayingKey, {
          playing: false,
        });
        this.forceEditorRefresh(editorState, contentState);
      }
    }
  }

  handleKeyCommand(command) {
    if (command === "split-block") {
      return this.splitBlock();
    }
    return "not-handled";
  }

  splitBlock() {
    let editorState = this.props.editorState;
    let contentState = editorState.getCurrentContent();
    let initialSelectionState = editorState.getSelection();

    contentState = Modifier.splitBlock(contentState, initialSelectionState);
    const currentBlockKey = initialSelectionState.getFocusKey();
    const currentBlock = contentState.getBlockForKey(currentBlockKey);
    const createdBlock = contentState.getBlockAfter(currentBlockKey);

    const currentData = currentBlock.getData();

    const createdBlockSelectionState = this.generateSelectionState(
      createdBlock.getKey(),
      createdBlock.getKey(),
      0,
      createdBlock.getKey(),
      0
    );

    contentState = Modifier.setBlockData(
      contentState,
      createdBlockSelectionState,
      currentData
    );

    contentState = Modifier.setBlockType(
      contentState,
      createdBlockSelectionState,
      createdBlock.getType() === "RepBubble" ? "CustomerBubble" : "RepBubble"
    );

    editorState = EditorState.push(editorState, contentState, "split-block");

    editorState = EditorState.forceSelection(
      editorState,
      createdBlockSelectionState
    );

    this.props.onChange(editorState);

    return "handled";
  }

  mergeWithBlock(direction) {
    const editorState = this.props.editorState;
    const contentState = editorState.getCurrentContent();
    const initialSelectionState = editorState.getSelection();
    const currentBlockKey = initialSelectionState.getAnchorKey();
    const currentBlock = contentState.getBlockForKey(currentBlockKey);

    let mergeCandidateBlock = null;

    if (
      direction === "forward" &&
      // When the cursor is to the right of the block's final character.
      initialSelectionState.getFocusOffset() === currentBlock.getLength()
    ) {
      mergeCandidateBlock = contentState.getBlockAfter(currentBlockKey);
    } else if (
      direction === "backward" &&
      // When the cursor is to the left of the block's first character.
      initialSelectionState.getFocusOffset() === 0
    ) {
      mergeCandidateBlock = contentState.getBlockBefore(currentBlockKey);
    }

    if (!mergeCandidateBlock) {
      return "not-handled";
    }

    const metadata = Object.fromEntries(mergeCandidateBlock.getData());

    const mergeCandidateBlockNeedsRevision =
      Boolean(metadata["eventQualityReasons"]) &&
      Boolean(metadata["eventQualityReasons"].length);

    if (mergeCandidateBlockNeedsRevision) {
      return "not-handled";
    } else {
      return "handled";
    }
  }

  render() {
    this.updatePlayingSnippetButton();

    const forcedState = EditorState.forceSelection(
      this.props.editorState,
      this.props.editorState.getSelection()
    );

    return (
      <Editor
        readOnly={this.props.readOnly}
        blockRenderMap={this.blockRenderMap}
        blockRendererFn={this.blockRendererFn}
        blockStyleFn={this.blockStyleFn}
        keyBindingFn={this.keyBindingFn}
        handleKeyCommand={this.handleKeyCommand}
        editorState={
          this.state.forceUpdate ? forcedState : this.props.editorState
        }
        onChange={this.props.onChange}
        spellCheck={true}
      />
    );
  }
}
